import { useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useCallback } from 'react';

import { type UUID } from '~/types/common';

import type { Company } from '~/data/company';
import { fetchCompanies, fetchCompany, queryKeysCompany } from '~/data/company';
import { useGetUserUtils } from '~/data/user';

import { addressToString } from '@vestigas/utils';

import { createLoadOptions, OptionPrimarySecondary } from '@vestigas/ui';

import { type Option, SelectServerDriven } from './SelectServerDriven';
import { createGetItemData } from './utils';

const getOptionLabel = ({ name }) => name;

/**
 * Formats select options like
 * ----------------------
 * <companyName>, <city>
 * ----------------------
 */
export const companyCityOptionLabel = ({
  data,
}: {
  data?: Pick<Company, 'name'> & Partial<Pick<Company, 'address'>>;
}) => {
  if (!data) {
    return '';
  }

  return [data.name, data.address?.city].filter(Boolean).join(', ');
};

/**
 * Formats select options like
 * ----------------------
 * <companyName>
 * <address>
 * ----------------------
 */
export const companyAddressOptionLabel = ({ data }: { data: Company }) => {
  return (
    <OptionPrimarySecondary
      primaryInformation={data.name}
      secondaryInformation={addressToString(data.address ?? {})}
    />
  );
};

type P = {
  readonly errors?: string;
  readonly formatOptionLabel?: (option: Option<Company>) => ReactNode;
  readonly isClearable?: boolean;
  readonly isDisabled?: boolean;
  readonly isMultiSelect?: boolean;
  readonly isRemovableValue?: (data: Option<Company>) => boolean;
  readonly onChange: (value: Array<{ id: UUID }>) => void;
  readonly placeholder?: string;
  readonly value: UUID[];
} & ComponentStyling;

export const MultiSelectCompanies = ({
  className,
  errors,
  formatOptionLabel,
  isClearable = true,
  isDisabled = false,
  isMultiSelect = true,
  isRemovableValue,
  placeholder = 'Firmen',
  onChange,
  style,
  value,
}: P) => {
  const queryClient = useQueryClient();
  const { UserUtils } = useGetUserUtils();

  /**
   * Loads the options for the multi-select dropdown.
   * @param {string} searchString - The search string entered by the user.
   * @param {unknown} loadedOptions - The already loaded options.
   * @param {{ page: number }} - The current page number.
   */
  const loadOptions = createLoadOptions<Company>({
    createQueryKey: ({ page, ...queryParams }) =>
      queryKeysCompany.getAll(queryParams),
    async fetchItems({ page, ...queryParams }) {
      const { data, hasNextPage } = await fetchCompanies(queryParams);

      return {
        data,
        hasNextPage,
      };
    },
    queryClient,
  });

  /**
   * Gets company data for a company with a given companyId.
   * Attempts to get it from the query cache and fetches it from the API
   * if the company data is not in the cache.
   * @param {string} companyId - The ID of the company to get.
   */
  const getItemData = createGetItemData<Company>({
    createQueryKey: (companyId) => queryKeysCompany.get(companyId),
    fetchItem: fetchCompany,
    queryClient,
    queryKeyBase: [queryKeysCompany.base()],
  });

  // This is just a glue function b/c PermissionGrantMultiPicker currently expects to pick the ID from an object in its onChange function. Should be refactored.
  const handleChange = useCallback(
    (newValues: string[] | string, data: Company[] | Company) => {
      onChange(
        Array.isArray(newValues)
          ? newValues.map((id) => ({ id }))
          : { id: newValues },
        data,
      );
    },
    [onChange],
  );

  return (
    <SelectServerDriven<Company>
      className={className}
      errors={errors}
      formatOptionLabel={formatOptionLabel}
      getItemData={getItemData}
      getOptionLabel={getOptionLabel}
      isClearable={isClearable}
      isDisabled={isDisabled}
      isItemSelectionAllowed={UserUtils.isCompanyReadAllowedUser}
      isMultiSelect={isMultiSelect}
      isRemovableValue={isRemovableValue}
      loadOptions={loadOptions}
      placeholder={placeholder}
      style={style}
      value={value}
      onChange={handleChange}
    />
  );
};
